import { FailureFilters, FilterMappings } from '@/types/filters';
import { Turbine, TurbineFilterList } from '@/types/turbine'
import { CensorRecord, Failure, FailureRecord, FailureFilter, OnyxData, OnyxDataRecord } from '@/types/failure';
import { filterOptionsService } from '@/services/api/filterOptions';

/**
 * Transform backend failure to frontend format for analysis
 */
export function transformFailureToRecord(failure: Failure): FailureRecord {
  return {
    failure_id: failure.failure_id,
    turbine_name: failure.turbine_name,
    site_name: failure.site_name || '',
    turbine_model: failure.model_name || '',
    component_major: failure.major_component_name || '',
    component_minor: failure.component_name || '',
    failure_mode: failure.failure_mode_name || '',
    outcome_name: failure.outcome_name || '',
    failure_date: failure.down_date || failure.detected_date,
    restoration_date: failure.actual_rts_date || failure.predicted_rts_date,
    detected_date: failure.detected_date,
    commissioning_date: failure.cod_date,
    install_date: failure.install_date,
    operation_time_years: failure.operational_days / 365.25, // Convert days to years
    case_id: failure.case_id || '',
    created_at: failure.created_at,
    updated_at: failure.updated_at,
    major_corrective: failure.major_corrective ? failure.major_corrective : 'N/A',
    planned_reactive: failure.planned_reactive ? failure.planned_reactive : 'N/A',
    record_source: failure.record_source,
  };
}

/**
 * Transform ONYX Data to frontend format for analysis
 */
export function transformONYXDataToRecord(onyxData: OnyxData): OnyxDataRecord {
  return {
    operation_time_years: onyxData.operational_days / 365.25,
    major_component_name: onyxData.major_component_name,
    sub_component_name: onyxData.sub_component_name,
    turbine_manufacturer_name: onyxData.turbine_manufacturer_name,
    turbine_model_name: onyxData.turbine_model_name,
    record_source: onyxData.record_source,
    is_censor: onyxData.is_censor
  }
}

/**
 * Transform a flat CensorRecord (from /failures/filter/analysis_filter) into
 * a FailureRecord suitable for merging with the failures list. The component
 * granularity has already been chosen server-side based on the request's
 * major-component filter; record_source flows through unchanged.
 */
export function transformCensorToFailureRecord(censor: CensorRecord): FailureRecord {
  const isMajorLevel = censor.component_name === censor.major_component_name;
  return {
    failure_id: '',
    turbine_name: censor.turbine_name || '',
    site_name: censor.site_name || '',
    turbine_model: censor.model_name || '',
    component_major: censor.major_component_name || '',
    component_minor: isMajorLevel ? '' : (censor.component_name || ''),
    failure_mode: '',
    outcome_name: '',
    failure_date: '',
    restoration_date: '',
    detected_date: '',
    commissioning_date: '',
    install_date: '',
    operation_time_years: censor.operational_days / 365.25,
    case_id: '',
    created_at: '',
    updated_at: '',
    major_corrective: '',
    planned_reactive: '',
    record_source: censor.record_source,
    is_censor: true,
  };
}

export function transformONYXToFailureRecord(onyxDataRecord: OnyxDataRecord): FailureRecord {
  return {
    failure_id: '',
    turbine_name: '',
    site_name: '',
    turbine_model: onyxDataRecord.turbine_model_name || '',
    component_major: onyxDataRecord.major_component_name || '',
    component_minor: onyxDataRecord.sub_component_name || '',
    failure_mode: '',
    outcome_name: '',
    failure_date: '',
    restoration_date: '',
    detected_date: '',
    commissioning_date: '',
    install_date: '',
    operation_time_years: onyxDataRecord.operation_time_years,
    case_id: '',
    created_at: '',
    updated_at: '',
    major_corrective: '',
    planned_reactive: '',
    record_source: onyxDataRecord.record_source,
    is_censor: onyxDataRecord.is_censor
  };
}

/**
 * Transform turbine to failure record
 */
export function transformTurbineToFailureRecord(t: Turbine): FailureRecord {
  const now = new Date().getTime();
  const cod_data = new Date(t.cod_date).getTime();
  const MS_IN_YEAR = 365.25 * 24 * 60 * 60 * 1000;
  const operationTime = now - cod_data
  return {
    failure_id: '',
    turbine_name: t.turbine_name,
    site_name: t.site_name || '',
    turbine_model: t.model_name || '',
    component_major: '',
    component_minor: '',
    failure_mode: '',
    outcome_name: '',
    failure_date: '',
    restoration_date: '',
    detected_date: '',
    commissioning_date: t.cod_date,
    install_date: '',
    operation_time_years: operationTime / MS_IN_YEAR,
    case_id: '',
    created_at: t.created_at,
    updated_at: t.updated_at,
    major_corrective: 'N/A',
    planned_reactive: 'N/A',
    record_source: 'cust_censor'
  };
}

// Helper function to map names to UUIDs
const mapNamesToUuids = (
  names: string[] | undefined,
  mappingKey: keyof FilterMappings,
  mappings: FilterMappings
): string[] | undefined => {
  if (!names || names.length === 0) return undefined;

  const mapping = mappings[mappingKey];
  const mapped = names.map((name) => mapping[name]).filter((uuid) => uuid !== undefined);

  return mapped.length > 0 ? mapped : undefined;
};

/**
 * Apply the sidebar's Advanced Filters (numeric range + date range) to an
 * already-fetched list. The backend ignores these range fields, so all filtering
 * for them happens client-side after fetch.
 *
 * Dates are compared on the YYYY-MM-DD prefix to avoid UTC/local timezone drift
 * and to make `dateTo` end-of-day inclusive (the user-typed string is the
 * authoritative calendar day, regardless of how the record's timestamp is
 * serialised). Records whose `dateField` is empty/null (e.g. censored or onyx
 * entries with no shutdown date) are not subject to the date filter — dropping
 * them would silently remove censored points that Weibull analysis depends on.
 */
export function applyRangeFilters<T>(
  records: T[],
  filters: FailureFilters,
  numericField: (record: T) => number | null | undefined,
  dateField: (record: T) => string | null | undefined,
): T[] {
  let result = records;

  if (Number.isFinite(filters.operationTimeMin)) {
    const min = filters.operationTimeMin as number;
    result = result.filter((r) => {
      const v = numericField(r);
      return v != null && v >= min;
    });
  }
  if (Number.isFinite(filters.operationTimeMax)) {
    const max = filters.operationTimeMax as number;
    result = result.filter((r) => {
      const v = numericField(r);
      return v != null && v <= max;
    });
  }
  if (filters.dateFrom) {
    const from = filters.dateFrom.slice(0, 10);
    result = result.filter((r) => {
      const v = dateField(r);
      if (!v) return true;
      return v.slice(0, 10) >= from;
    });
  }
  if (filters.dateTo) {
    const to = filters.dateTo.slice(0, 10);
    result = result.filter((r) => {
      const v = dateField(r);
      if (!v) return true;
      return v.slice(0, 10) <= to;
    });
  }

  return result;
}

/**
 * Transform frontend filters (names) to backend filters (UUIDs)
 */
export async function transformFiltersToFailureFilter(
  frontendFilters: FailureFilters
): Promise<FailureFilter> {
  // Get the mappings from the service
  const mappings = await filterOptionsService.getFilterMappings();

  // Build the filter object with properties only when they have values
  const filter: FailureFilter = {};

  // Only add properties if they have values
  const failureModes = frontendFilters.failureModes;
  if (failureModes && failureModes.length > 0) {
    filter.failure_mode_name = failureModes;
  }

  const siteIds = mapNamesToUuids(frontendFilters.sites, 'sites', mappings);
  if (siteIds) {
    filter.site_id = siteIds;
  }

  const componentIds = mapNamesToUuids(frontendFilters.componentsMinor, 'components', mappings);
  if (componentIds) {
    filter.component_id = componentIds;
  }

  const majorComponentIds = mapNamesToUuids(frontendFilters.componentsMajor, 'majorComponents', mappings);
  if (majorComponentIds) {
    filter.major_component_id = majorComponentIds;
  }

  const outcomeIds = mapNamesToUuids(frontendFilters.outcomeName, 'outcomeName', mappings);
  if (outcomeIds) {
    filter.outcome_id = outcomeIds;
  }

  const excludedCaseIds = frontendFilters.excludedCaseIds;
  if (excludedCaseIds && excludedCaseIds.length > 0) {
    filter.case_id = excludedCaseIds;
  }

  const modelIds = mapNamesToUuids(frontendFilters.turbineModels, 'models', mappings);
  if (modelIds) {
    filter.model_id = modelIds;
  }

  return filter;
}


/**
 * Transform frontend filters (names) to backend filters (UUIDs) synchronously
 * This version uses cached mappings and throws if not available
 */
export function transformFiltersToFailureFilterSync(
  frontendFilters: FailureFilters,
  mappings: FilterMappings
): FailureFilter {

  // Build the filter object with properties only when they have values
  const filter: FailureFilter = {};

  // Only add properties if they have values
  const failureModes = frontendFilters.failureModes;
  if (failureModes && failureModes.length > 0) {
    filter.failure_mode_name = failureModes;
  }

  const siteIds = mapNamesToUuids(frontendFilters.sites, 'sites', mappings);
  if (siteIds) {
    filter.site_id = siteIds;
  }

  const componentIds = mapNamesToUuids(frontendFilters.componentsMinor, 'components', mappings);
  if (componentIds) {
    filter.component_id = componentIds;
  }

  const majorComponentIds = mapNamesToUuids(frontendFilters.componentsMajor, 'majorComponents', mappings);
  if (majorComponentIds) {
    filter.major_component_id = majorComponentIds;
  }

  const outcomeIds = mapNamesToUuids(frontendFilters.outcomeName, 'outcomeName', mappings);
  if (outcomeIds) {
    filter.outcome_id = outcomeIds;
  }

  const excludedCaseIds = frontendFilters.excludedCaseIds;
  if (excludedCaseIds && excludedCaseIds.length > 0) {
    filter.case_id = excludedCaseIds;
  }

  const modelIds = mapNamesToUuids(frontendFilters.turbineModels, 'models', mappings);
  if (modelIds) {
    filter.model_id = modelIds;
  }

  return filter;
}

/**
 * Transform frontend filters (names) to backend filters (UUIDs)
 */
export async function transformFiltersToTurbineFilterList(
  frontendFilters: FailureFilters
): Promise<TurbineFilterList> {
  // Get the mappings from the service
  const mappings = await filterOptionsService.getFilterMappings();

  // Build the filter object with properties only when they have values
  const filter: TurbineFilterList = {};

  // Only add properties if they have values
  const siteIds = mapNamesToUuids(frontendFilters.sites, 'sites', mappings);
  if (siteIds) {
    filter.site_id = siteIds;
  }

  const modelIds = mapNamesToUuids(frontendFilters.turbineModels, 'models', mappings);
  if (modelIds) {
    filter.model_id = modelIds;
  }

  return filter;
}


/**
 * Sort function for operation time.
 */
export function sortByOperationTimeDescending(a, b) {
  return a.operation_time_years > b.operation_time_years ? -1 : a.operation_time_years < b.operation_time_years ? 1 : 0;
}

/**
 * Remove null, undefined, and empty strings from a Set
 */
export function cleanSet(setToClean: Set<string>): Set<string> {
  if (setToClean.size > 0) {
    const itemsToRemove = [null, undefined, ''];

    for (const key of itemsToRemove) {
      if (setToClean.has(key)) {
        setToClean.delete(key);
      }
    }

  }
  return setToClean;
}
